import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Database, Sparkles, Trash2, RefreshCw, AlertTriangle, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Populate / clear demo data. Every seeded document is tagged demo_seed=true, so
// Clear removes only the demo set and never touches real enrolled agents.

export default function DemoData() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "msp_owner";
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  // One-click sign-in visibility (fleet standard). This is a SEPARATE switch
  // from demo_seed above: it gates the login page's buttons, which sign in as
  // seed_ref-tagged @test.com accounts -- not the telemetry this page seeds.
  const [demoLogin, setDemoLogin] = useState(null);

  const load = () => {
    api.get("/demo/status").then(r => setStatus(r.data)).catch(() => setStatus(null));
    if (isAdmin) api.get("/admin/demo-login").then(r => setDemoLogin(r.data)).catch(() => setDemoLogin(null));
  };
  useEffect(() => { load(); }, [isAdmin]);

  // Hiding the buttons DISABLES them: the same setting gates /auth/demo-accounts
  // and /auth/demo-login on the server, so this is not merely a display toggle.
  // It can only ever take away -- with DEMO_QUICK_LOGIN off for the deployment,
  // this switch cannot turn the feature on.
  const setDemoLoginVisible = async (visible) => {
    try {
      const { data } = await api.put("/admin/demo-login", { visible });
      setDemoLogin(data);
      toast.success(visible ? "One-click sign-in shown" : "One-click sign-in hidden and disabled");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Admin only" : "Could not update");
    }
  };

  const seed = async () => {
    setBusy("seed");
    try {
      const { data } = await api.post("/demo/seed");
      toast.success(`Seeded ${data.total} demo records`);
      await load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Admin only" : "Seed failed");
    } finally { setBusy(""); }
  };

  const clear = async () => {
    if (!window.confirm("Clear all demo data? Real enrolled agents are not affected.")) return;
    setBusy("clear");
    try {
      const { data } = await api.post("/demo/clear");
      toast.success(`Cleared ${data.total} demo records`);
      await load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Admin only" : "Clear failed");
    } finally { setBusy(""); }
  };

  const counts = status?.counts || {};
  const nonZero = Object.entries(counts).filter(([, v]) => v > 0);

  return (
    <div className="p-6 md:p-8 max-w-[900px] mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-center">
          <Database className="text-violet-400" size={18} />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-zinc-50">Demo Data</h1>
          <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">Populate or clear a sample estate</div>
        </div>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 mt-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm text-zinc-200">
              {status?.seeded
                ? <><span className="text-violet-300 font-mono">{status.total}</span> demo records currently loaded</>
                : "No demo data loaded"}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              Populates clients, agents with full telemetry, vulnerabilities, incidents, backups,
              evidence and audit history — so the executive dashboards, ISO report and monitoring fill in.
            </div>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-zinc-200 text-xs">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={seed} disabled={!!busy}
            className="inline-flex items-center gap-2 bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/40 text-violet-200 rounded-md px-4 py-2.5 text-sm disabled:opacity-50">
            <Sparkles size={16} /> {busy === "seed" ? "Populating…" : "Populate demo data"}
          </button>
          <button onClick={clear} disabled={!!busy || !status?.seeded}
            className="inline-flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 rounded-md px-4 py-2.5 text-sm disabled:opacity-40">
            <Trash2 size={16} /> {busy === "clear" ? "Clearing…" : "Clear demo data"}
          </button>
        </div>
      </div>

      {nonZero.length > 0 && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 mt-4">
          <div className="text-sm font-medium text-zinc-200 mb-3">Loaded records</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {nonZero.map(([k, v]) => (
              <div key={k} className="bg-zinc-900/40 border border-zinc-900 rounded px-3 py-2">
                <div className="font-mono text-lg text-zinc-100">{v}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-start gap-2 text-[11px] text-zinc-600 mt-4">
        <AlertTriangle size={14} className="text-amber-500/70 shrink-0 mt-0.5" />
        <span>Demo records are tagged internally and cleared as a set. Real agents that enrol through the
        installer are never affected by either button. Populate replaces any existing demo set.</span>
      </div>

      {isAdmin && demoLogin && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 mt-8" data-testid="demo-login-settings">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0">
                <KeyRound className="text-blue-400" size={16} />
              </div>
              <div>
                <div className="text-sm font-medium text-zinc-200">One-click sign-in</div>
                <div className="text-xs text-zinc-500 mt-1 max-w-md">
                  Buttons on the login page that sign a visitor straight in as a seeded
                  demo account, no password typed. Hiding this DISABLES the buttons on the
                  server, not just on the screen. Your own sign-in is unaffected.
                </div>
              </div>
            </div>
            <button
              onClick={() => setDemoLoginVisible(!demoLogin.visible)}
              disabled={!demoLogin.env_enabled}
              data-testid="demo-login-toggle"
              className={
                "shrink-0 inline-flex items-center gap-2 rounded-md px-3.5 py-2 text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed " +
                (demoLogin.visible
                  ? "bg-blue-500/15 border-blue-500/40 text-blue-200 hover:bg-blue-500/25"
                  : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-900/70")
              }
            >
              {demoLogin.visible ? "Shown — click to hide" : "Hidden — click to show"}
            </button>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-600 mt-4">
            {demoLogin.env_enabled
              ? `${demoLogin.account_count} seed-tagged account${demoLogin.account_count === 1 ? "" : "s"} eligible`
              : demoLogin.note}
          </div>
        </div>
      )}
    </div>
  );
}
